import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { formatDate, formatToZMW, rootUrl, sumOfArray } from '../../../helpers';
import { useAuth } from '../../../context/auth-context';
import { FaBook, FaChartBar, FaEye, FaFileExcel, FaFilePdf, FaFolderOpen, FaPrint, FaSpinner, FaTimes, FaBuilding, FaMoneyBillWave, FaArrowUp, FaArrowDown, FaList } from 'react-icons/fa';
import { useOutletContext } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

export default function SummaryCash({ startDate, endDate, triggerFetch }) {
  const [cash, setCash] = useState([]);
  const [open, setOpen] = useState([]);
  const [cashbook, setCashbook] = useState([]);
  const [selectedCashbook, setSelectedCashbook] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const auth = useAuth();
  const {allData, setAllData} = useOutletContext();

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await axios.get(rootUrl(`/getSingleCashByDate/${auth.user?.cid}~${startDate}~${endDate}`));
      setOpen(data.data.data.balances);
      setCash(data.data.data.cash);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const componentRef = useRef(null);
  
  const getSelectedSummary = () => {
    const openingBalance = parseFloat(open.find((a) => a.bank == selectedCashbook?.id)?.opening_balance) || 0;
    const totalIncome = parseFloat(cash.find((a) => a.bank == selectedCashbook?.id && a.type == 'income')?.amount) || 0;
    const totalExpenses = parseFloat(cash.find((a) => a.bank == selectedCashbook?.id && a.type == 'expense')?.amount) || 0;
    return {
      openingBalance,
      totalIncome,
      totalExpenses,
      balance: openingBalance + totalIncome - totalExpenses
    };
  };

  const getDisplayRows = () => (groupedView ? groupByVoucher(calculateBalance()) : calculateBalance());

  function handleExcel() {
    if (!selectedCashbook) return;

    const summary = getSelectedSummary();
    const rows = getDisplayRows();
    const totalDebit = sumOfArray(rows.map((a) => (groupedView ? a.totalIncome : (a.type === 'income' ? parseFloat(a.amount) : 0))));
    const totalCredit = sumOfArray(rows.map((a) => (groupedView ? a.totalExpense : (a.type === 'expense' ? parseFloat(a.amount) : 0))));

    const worksheetRows = [
      [allData.council[0]?.name || ''],
      [`${selectedCashbook?.name || ''} : ${selectedCashbook?.account_no || ''}`],
      [`Cash book from ${formatDate(startDate)} to ${formatDate(endDate)}`],
      [],
      ['Summary', 'Amount'],
      ['Opening Balance', summary.openingBalance],
      ['Total Income', summary.totalIncome],
      ['Total Expenses', summary.totalExpenses],
      ['Balance', summary.balance],
      [],
      groupedView
        ? ['Date', 'Description', 'Voucher', 'Cheque', 'Payee', 'Debit', 'Credit', 'Balance']
        : ['Date', 'COA', 'Description', 'Voucher', 'Cheque', 'Payee', 'Debit', 'Credit', 'Balance']
    ];

    rows.forEach((item) => {
      const payee = (() => {
        const payeeId = item.paye;
        if (item.firstType === 'income') {
          const customer = allData?.suppliers?.find((a) => a.id == payeeId);
          return customer ? customer.name : '';
        }
        const supplier = allData?.suppliers?.find((a) => a.id == payeeId);
        return supplier?.name || '';
      })();

      const debit = groupedView ? item.totalIncome : (item.type === 'income' ? parseFloat(item.amount) || 0 : 0);
      const credit = groupedView ? item.totalExpense : (item.type === 'expense' ? parseFloat(item.amount) || 0 : 0);
      const balance = groupedView ? (parseFloat(item.balance) || 0) : (parseFloat(item.balance) || 0);

      if (groupedView) {
        worksheetRows.push([
          item.date?.substring(0, 10) || '',
          item.description || '',
          item.voucher || '',
          item.cheque || '',
          payee,
          debit,
          credit,
          balance
        ]);
      } else {
        const coaCode = allData?.budget?.find((a) => a.id === item.coa_id)?.coa || '';
        const coaName = allData.coa?.find((b) => b.account == coaCode)?.name || '';
        worksheetRows.push([
          item.date?.substring(0, 10) || '',
          coaCode ? `${coaCode} : ${coaName}` : '',
          item.description || '',
          item.voucher || '',
          item.cheque || '',
          payee,
          debit,
          credit,
          balance
        ]);
      }
    });

    worksheetRows.push([]);
    worksheetRows.push(['Grand Total', '', '', '', '', totalDebit, totalCredit, '']);

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Cashbook');
    XLSX.writeFile(workbook, `cashsummary_${selectedCashbook?.name || 'report'}.xlsx`);
  }

  const handleExportToPDF = async () => {
    const input = componentRef.current;
    if (!input || !selectedCashbook) return;

    const clone = input.cloneNode(true);
    const exportContainer = document.createElement('div');

    exportContainer.style.position = 'fixed';
    exportContainer.style.left = '-100000px';
    exportContainer.style.top = '0';
    exportContainer.style.background = '#fff';
    exportContainer.style.padding = '24px';
    exportContainer.style.zIndex = '-1';

    clone.style.overflow = 'visible';
    clone.style.maxHeight = 'none';
    clone.style.height = 'auto';
    clone.style.width = 'max-content';

    clone.querySelectorAll('.overflow-x-auto, .overflow-auto').forEach((el) => {
      el.style.overflow = 'visible';
      el.style.maxHeight = 'none';
    });

    clone.querySelectorAll('table').forEach((table) => {
      table.style.width = 'max-content';
      table.style.minWidth = '100%';
      table.style.tableLayout = 'auto';
    });

    exportContainer.appendChild(clone);
    document.body.appendChild(exportContainer);

    try {
      const contentWidth = Math.ceil(clone.scrollWidth);
      const contentHeight = Math.ceil(clone.scrollHeight);

      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        width: contentWidth,
        height: contentHeight,
        windowWidth: contentWidth,
        windowHeight: contentHeight,
        scrollX: 0,
        scrollY: 0
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`Cashbook_${selectedCashbook?.name || 'report'}_${startDate}_${endDate}.pdf`);
    } finally {
      document.body.removeChild(exportContainer);
    }
  };

  const handlePrint = () => {
    const printContent = componentRef.current;
    const printWindow = window.open("", "", "width=800,height=600");
    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>Cashbook</title>
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            @page {
              size: A4 landscape;
              margin: 10mm;
            }
            @media print {
              body {
                margin: 0;
                font-size: 10px;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              * {
                box-sizing: border-box;
              }
              .overflow-x-auto,
              .overflow-auto {
                overflow: visible !important;
              }
              .max-h-\\[90vh\\] {
                max-height: none !important;
              }
              table {
                width: 100% !important;
                table-layout: auto !important;
                border-collapse: collapse !important;
              }
              th, td {
                white-space: normal !important;
                word-break: break-word !important;
                padding: 4px 6px !important;
                font-size: 9px !important;
                line-height: 1.25 !important;
              }
            }
          </style>
        </head>
        <body class="p-4">${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
  
    // Ensure Tailwind is applied before printing
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  useEffect(() => {
    if (triggerFetch && startDate && endDate) {
      fetchData();
    }
  }, [triggerFetch]);

  const handleAction = async (id) => {
    const selected = allData.bank.find((res) => res.id === id);
    setSelectedCashbook(selected);
    setIsModalOpen(true);
    
    try {
      const data = await axios.get(rootUrl(`/getBankCash/${id}~${startDate}~${endDate}`));
      setCashbook(data.data.data);
    } catch (error) {
      console.error('Error fetching bank cash data:', error);
    }
  };

  const calculateBalance = () => {
    // Get the initial opening balance
    let balance = parseFloat(open.find((a) => a.bank == cashbook[0]?.bank)?.opening_balance) || 0;
  
    // Sort the cashbook data by date in ascending order
    const sortedCashbook = [...cashbook].sort((a, b) => new Date(a.date) - new Date(b.date));
  
    return sortedCashbook.map((row) => {
      balance += row.type === 'income' ? parseFloat(row.amount) : -parseFloat(row.amount);
      return { ...row, balance };
    });
  };
  const [groupedView, setGroupedView] = useState(false);


  const groupByVoucher = (data) => {
    const grouped = {};
    let runningBalance = 0;
  
    data.forEach(item => {
      if (!grouped[item.voucher]) {
        grouped[item.voucher] = {
          voucher: item.voucher,
          date: item.date,
          cheque: item.cheque,
          description: item.description,
          paye: item.paye,
          firstType: item.type,
          entries: [],
          totalIncome: 0,
          totalExpense: 0,
        };
      }
  
      grouped[item.voucher].entries.push(item);
  
      if (item.type === 'income') {
        grouped[item.voucher].totalIncome += parseFloat(item.amount);
      } else if (item.type === 'expense') {
        grouped[item.voucher].totalExpense += parseFloat(item.amount);
      }
    });
  
    // Now, convert to array and compute balance
    const groupedArray = Object.values(grouped).map(group => {
      runningBalance += group.totalIncome - group.totalExpense;
      return {
        ...group,
        balance: runningBalance,
      };
    });
  
    return groupedArray;
  };
  
  

  useEffect(()=>{
    calculateBalance()
  },[cashbook])
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-blue-950 rounded-lg">
          <FaBook className="text-white text-xl" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">Cashbooks Summary</h2>
          <p className="text-sm text-gray-500">Overview of all cashbook balances</p>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col justify-center items-center h-64 bg-gray-50 rounded-xl border border-gray-200">
          <FaSpinner className="text-blue-950 text-5xl mb-4 animate-spin" />
          <p className="text-gray-600 text-lg">Loading cashbook data...</p>
        </div>
      ) : allData.bank && allData.bank.length > 0 ? (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-950 to-blue-900 text-white">
                    <th className="px-6 py-3 border-b border-blue-700 text-left font-semibold text-sm">Cashbook</th>
                    <th className="px-6 py-3 border-b border-blue-700 text-right font-semibold text-sm">Opening Balance</th>
                    <th className="px-6 py-3 border-b border-blue-700 text-right font-semibold text-sm">Total Income</th>
                    <th className="px-6 py-3 border-b border-blue-700 text-right font-semibold text-sm">Total Expenses</th>
                    <th className="px-6 py-3 border-b border-blue-700 text-right font-semibold text-sm">Balance</th>
                    <th className="px-6 py-3 border-b border-blue-700 text-center font-semibold text-sm">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {allData.bank.map((res, index) => {
                    const openingBalance = parseFloat(open.find((a) => a.bank == res.id)?.opening_balance) || 0;
                    const totalIncome = parseFloat(cash.find((a) => a.bank == res.id && a.type == 'income')?.amount) || 0;
                    const totalExpenses = parseFloat(cash.find((a) => a.bank == res.id && a.type == 'expense')?.amount) || 0;
                    const balance = (parseFloat(open.find((a) => a.bank == res.id)?.opening_balance) || 0) + totalIncome - totalExpenses;

                    return (
                      <tr key={res.id} className={`border-b border-gray-200 hover:bg-blue-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="px-6 py-3 text-sm font-medium text-gray-800">{res.name}</td>
                        <td className="px-6 py-3 text-sm text-right text-gray-700">{formatToZMW(openingBalance)}</td>
                        <td className="px-6 py-3 text-sm text-right text-green-700 font-medium">{formatToZMW(totalIncome)}</td>
                        <td className="px-6 py-3 text-sm text-right text-red-700 font-medium">{formatToZMW(totalExpenses)}</td>
                        <td className="px-6 py-3 text-sm text-right font-semibold text-gray-900">{formatToZMW(balance)}</td>
                        <td className="px-6 py-3 text-center">
                          <button 
                            onClick={() => handleAction(res.id)} 
                            className="p-2 bg-blue-950 text-white rounded-lg hover:bg-blue-900 transition-colors"
                            title="View Details"
                          >
                            <FaEye className="text-sm" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                  {/* Totals Row */}
                  <tr className="bg-gradient-to-r from-blue-950 to-blue-900 text-white border-t-4 border-blue-800">
                    <td className="px-6 py-4 text-left font-bold text-base">Grand Total</td>
                    <td className="px-6 py-4 text-right font-bold text-base">
                      {formatToZMW(
                        allData.bank.reduce((sum, res) => sum + (parseFloat(open.find((a) => a.bank == res.id)?.opening_balance) || 0), 0)
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-bold text-base text-green-300">
                      {formatToZMW(
                        allData.bank.reduce((sum, res) => sum + (parseFloat(cash.find((a) => a.bank == res.id && a.type == 'income')?.amount) || 0), 0)
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-bold text-base text-red-300">
                      {formatToZMW(
                        allData.bank.reduce((sum, res) => sum + (parseFloat(cash.find((a) => a.bank == res.id && a.type == 'expense')?.amount) || 0), 0)
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-bold text-base">
                      {formatToZMW(
                        (
                          allData.bank.reduce((sum, res) => sum + (parseFloat(open.find((a) => a.bank == res.id)?.opening_balance) || 0), 0)
                        )+
                        allData.bank.reduce(
                          (sum, res) =>
                            sum +
                            (
                              (parseFloat(cash.find((a) => a.bank == res.id && a.type == 'income')?.amount) || 0) -
                              (parseFloat(cash.find((a) => a.bank == res.id && a.type == 'expense')?.amount) || 0)),
                          0
                        )
                      )}
                    </td>
                    <td className="px-6 py-4 text-center"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Report Modal */}
          {isModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
              <div className="bg-white relative rounded-2xl w-full max-w-6xl shadow-2xl max-h-[90vh] flex flex-col">
                {/* Modal Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-blue-950 to-blue-900 text-white rounded-t-2xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                      <FaBuilding className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Cashbook Report</h3>
                      <p className="text-sm text-blue-100">{selectedCashbook?.name}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setIsModalOpen(false)}
                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                    title="Close"
                  >
                    <FaTimes className="text-white text-xl" />
                  </button>
                </div>

                {/* Action Buttons */}
                <div className='flex items-center justify-end gap-2 p-4 bg-gray-50 border-b border-gray-200'>
                  <button
                    onClick={handlePrint}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium text-sm flex items-center gap-2 shadow-sm"
                    title="Print"
                  >
                    <FaPrint />
                    <span>Print</span>
                  </button>
                  <button
                    onClick={handleExcel}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm flex items-center gap-2 shadow-sm"
                    title="Export to Excel"
                  >
                    <FaFileExcel />
                    <span>Excel</span>
                  </button>
                  <button
                    onClick={handleExportToPDF}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm flex items-center gap-2 shadow-sm"
                    title="Export to PDF"
                  >
                    <FaFilePdf />
                    <span>PDF</span>
                  </button>
                  <button
                    onClick={() => setGroupedView(prev => !prev)}
                    className="px-4 py-2 bg-blue-950 text-white rounded-lg hover:bg-blue-900 transition-colors font-medium text-sm flex items-center gap-2 shadow-sm"
                  >
                    <FaList />
                    <span>{groupedView ? "Show Full List" : "Group by Voucher"}</span>
                  </button>
                </div>


             

                {/* Modal Content */}
                <div ref={componentRef} className='overflow-auto p-6 flex-1'>
                  {/* Report Header */}
                  <div className="text-center mb-6 bg-gradient-to-r from-blue-950 to-blue-900 text-white py-6 px-4 rounded-xl">
                    <h2 className="font-bold text-xl md:text-2xl mb-2">
                      {allData.council[0]?.name.toUpperCase()}
                    </h2>
                    <h3 className="text-lg md:text-xl font-semibold mb-2">
                      {selectedCashbook?.name} : {selectedCashbook?.account_no}
                    </h3>
                    <p className="text-base font-medium opacity-90">
                      Cash book from {formatDate(startDate)} to {formatDate(endDate)}
                    </p>
                  </div>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Opening Balance</p>
                          <p className="text-lg font-bold text-gray-900 mt-1">{formatToZMW(parseFloat(open.find((a) => a.bank == selectedCashbook?.id)?.opening_balance) || 0)}</p>
                        </div>
                        <div className="p-2 bg-gray-200 rounded-lg">
                          <FaMoneyBillWave className="text-gray-600" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-green-700 uppercase tracking-wide">Total Income</p>
                          <p className="text-lg font-bold text-green-900 mt-1">{formatToZMW(parseFloat(cash.find((a) => a.bank == selectedCashbook?.id && a.type == 'income')?.amount) || 0)}</p>
                        </div>
                        <div className="p-2 bg-green-500 rounded-lg">
                          <FaArrowUp className="text-white" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-red-700 uppercase tracking-wide">Total Expenses</p>
                          <p className="text-lg font-bold text-red-900 mt-1">{formatToZMW(parseFloat(cash.find((a) => a.bank == selectedCashbook?.id && a.type == 'expense')?.amount) || 0)}</p>
                        </div>
                        <div className="p-2 bg-red-500 rounded-lg">
                          <FaArrowDown className="text-white" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">Balance</p>
                          <p className="text-lg font-bold text-blue-900 mt-1">{formatToZMW((parseFloat(open.find((a) => a.bank == selectedCashbook?.id)?.opening_balance) || 0) + (parseFloat(cash.find((a) => a.bank == selectedCashbook?.id && a.type == 'income')?.amount) || 0) - (parseFloat(cash.find((a) => a.bank == selectedCashbook?.id && a.type == 'expense')?.amount) || 0))}</p>
                        </div>
                        <div className="p-2 bg-blue-500 rounded-lg">
                          <FaChartBar className="text-white" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Transactions Table */}
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className="bg-gradient-to-r from-blue-950 to-blue-900 text-white">
                            <th className="px-4 py-3 text-left font-semibold text-sm">Date</th>
                            {!groupedView && <th className="px-4 py-3 text-left font-semibold text-sm">COA</th>}
                            <th className="px-4 py-3 text-left font-semibold text-sm">Description</th>
                            <th className="px-4 py-3 text-left font-semibold text-sm">Voucher</th>
                            <th className="px-4 py-3 text-left font-semibold text-sm">Cheque</th>
                            <th className="px-4 py-3 text-left font-semibold text-sm">Payee</th>
                            <th className="px-4 py-3 text-right font-semibold text-sm">Debit</th>
                            <th className="px-4 py-3 text-right font-semibold text-sm">Credit</th>
                            <th className="px-4 py-3 text-right font-semibold text-sm">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cashbook.length > 0 ? (
                            (groupedView ? groupByVoucher(calculateBalance()) : calculateBalance()).map((item, index) => (
                              <tr key={index} className="border-b border-gray-200 hover:bg-blue-50 transition-colors">
                                <td className="px-4 py-2.5 text-sm text-gray-700">{item.date.substring(0, 10)}</td>
                                {!groupedView && (
                                  <td className="px-4 py-2.5 text-sm text-gray-700">
                                    {allData?.budget?.find((a) => a.id === item.coa_id)?.coa || ''} :
                                    {allData.coa?.find(b => b.account == allData?.budget?.find((a) => a.id === item.coa_id)?.coa)?.name}
                                  </td>
                                )}
                                <td className="px-4 py-2.5 text-sm text-gray-600 align-top break-words whitespace-normal min-w-[14rem]">{item.description}</td>
                                <td className="px-4 py-2.5 text-sm text-gray-700">{item.voucher}</td>
                                <td className="px-4 py-2.5 text-sm text-gray-700">{item.cheque}</td>
                                <td className="px-4 py-2.5 text-sm text-gray-700">
                                  {(() => {
                                    const payeeId = item.paye;
                                    if (item.firstType === "income") {
                                      const customer = allData?.suppliers?.find((a) => a.id == payeeId);
                                      return customer ? `${customer.name}` : "";
                                    } else {
                                      const supplier = allData?.suppliers?.find((a) => a.id == payeeId);
                                      return supplier?.name || "";
                                    }
                                  })()}
                                </td>
                                <td className="px-4 py-2.5 text-sm text-right text-green-700 font-medium">{formatToZMW(groupedView ? item.totalIncome : (item.type === 'income' ? item.amount : 0))}</td>
                                <td className="px-4 py-2.5 text-sm text-right text-red-700 font-medium">{formatToZMW(groupedView ? item.totalExpense : (item.type === 'expense' ? item.amount : 0))}</td>
                                <td className="px-4 py-2.5 text-sm text-right text-gray-800 font-semibold">{formatToZMW(groupedView ? item.balance : (item.balance || 0))}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={groupedView ? 8 : 9} className="text-center py-12">
                                <div className="flex flex-col justify-center items-center">
                                  <div className="p-4 bg-gray-100 rounded-full mb-4">
                                    <FaFolderOpen className="text-gray-400 text-5xl" />
                                  </div>
                                  <p className="text-gray-600 text-lg font-medium">No data available</p>
                                </div>
                              </td>
                            </tr>
                          )}
                          {/* Totals Row */}
                          <tr className="bg-gradient-to-r from-blue-950 to-blue-900 text-white border-t-4 border-blue-800">
                            <th colSpan={groupedView ? 5 : 6} className="px-4 py-3 text-left font-bold text-base">Grand Total</th>
                            <th className="px-4 py-3 text-right font-bold text-base">
                              {formatToZMW(
                                sumOfArray(
                                  (groupedView ? groupByVoucher(calculateBalance()) : calculateBalance())
                                    .map(a => groupedView ? a.totalIncome : (a.type === 'income' ? parseFloat(a.amount) : 0))
                                )
                              )}
                            </th>
                            <th className="px-4 py-3 text-right font-bold text-base">
                              {formatToZMW(
                                sumOfArray(
                                  (groupedView ? groupByVoucher(calculateBalance()) : calculateBalance())
                                    .map(a => groupedView ? a.totalExpense : (a.type === 'expense' ? parseFloat(a.amount) : 0))
                                )
                              )}
                            </th>
                            <th className="px-4 py-3 text-right font-bold text-base"></th>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <FaSpinner className="spinner" />
      )}
    </div>
    
  );
}
